import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { Helper } from '../../components/ui/Helper';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { OrdinaFiltra, useOrdina, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { useUrlState } from '../../hooks/useUrlState';
import { useDocActions } from './useDocActions';
import { AVVISO_INCASSO } from './incasso';
import { useAdminActions } from '../admin/useAdminActions';
import { TrashPopover } from '../admin/TrashPopover';
import { SettlePopover } from '../admin/SettlePopover';
import { canReadAdmin, daysFromToday } from '../admin/adminApi';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  documentsQuery, type DocSource, type DocKind, type InvoiceItem, type QuoteItem, type UnifiedRow,
} from '../../lib/scadenziario';
import { InvoiceType, InvoiceStatus, QuoteStatus, ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';

/**
 * Documenti — la lista di fatture, note di credito e preventivi.
 *
 * Vive in Amministrazione › Fatture, nelle viste "Fatture emesse da noi"
 * (scope 'fiscali') e "Preventivi" (scope 'preventivi'), senza fascia di numeri:
 * i totali di chi ci deve pagare stanno in Fatture da incassare.
 *   filterbar → pill tipo + "Altri tipi di documento" · cerca · pill stato +
 *               "Altri stati" · contatore · "Nuova fattura o preventivo"
 *   tabella   → Tipo di documento (solo su "Tutti i documenti") · Numero e
 *               cliente · Progetto · Emesso il · Da pagare entro · Totale con
 *               IVA · Stato del pagamento · azione contestuale
 *
 * "Segna incassata" apre lo stesso popover della rata di Fatture da incassare
 * e chiude la prossima rata aperta (POST /invoices/:id/mark-paid con
 * paymentId): prima cambiava solo lo stato della fattura e la rata restava da
 * incassare. Registra e basta: non parte nessun pagamento e FattureInCloud
 * non cambia (per questo non si chiama più "Incassa").
 *
 * Filtri e paginazione sono SUL SERVER (594 fatture in DB: una lista
 * client-side su /invoices?pageSize=200 ne mostrerebbe solo un pezzo).
 * Ogni filtro = una query sola, quindi il tipo sceglie anche l'endpoint:
 * i preventivi stanno su /quotes, il resto su /invoices.
 *
 * Stato in URL (deep-link / bookmark):
 *   type · status · q  — es. /admin/fatture?vista=documenti&type=FATTURA,
 *   /admin/fatture?vista=preventivi&status=INVIATO
 *
 * `showHistory` arriva dal filtro "Anche prima del 2026" dell'Amministrazione:
 * senza, la lista parte dalla data di taglio (OrgSettings.adminCutoffDate).
 */

const PAGE_SIZE = 25;

/**
 * Chip di stato nella riga: brevi, perché la riga dice già di che documento si
 * tratta (regola etichette di SPEC.md). Parole del glossario § 5: "Aperto dal
 * cliente" al posto di "Visto", "Diventato fattura" al posto di "Convertito".
 *
 * Il rosso (warn e alert sono tutti e due rossi, index.css) solo per quello
 * che è in ritardo: una fattura emessa che deve ancora scadere è "Da
 * incassare" e basta, grigia (Lorenzo, 24/9: non "Emessa" in rosso). Se la
 * data per pagare è passata, la riga dice "In ritardo" anche quando lo stato
 * a sistema è ancora EMESSA (vedi DocumentRow).
 */
export const STATUS_INFO: Record<string, { label: string; tone: 'ok' | 'warn' | 'alert' | 'info' | 'ghost' | 'default' }> = {
  BOZZA:        { label: 'Bozza',               tone: 'ghost' },
  EMESSA:       { label: 'Da incassare',        tone: 'info' },
  INVIATA:      { label: 'Inviata, da incassare', tone: 'info' },
  PAGATA:       { label: 'Pagata',              tone: 'ok' },
  SCADUTA:      { label: 'In ritardo',          tone: 'alert' },
  ANNULLATA:    { label: 'Annullata',           tone: 'ghost' },
  INVIATO:      { label: 'Inviato, da firmare', tone: 'info' },
  VISUALIZZATO: { label: 'Aperto dal cliente',  tone: 'info' },
  ACCETTATO:    { label: 'Firmato',             tone: 'ok' },
  RIFIUTATO:    { label: 'Rifiutato',           tone: 'alert' },
  SCADUTO:      { label: 'Scaduto senza firma', tone: 'ghost' },
  CONVERTITO:   { label: 'Diventato fattura',   tone: 'ok' },
};

interface SourceDef { key: DocSource; label: string; icon: IconName; title: string }

/** Tipi di documento con parole intere (glossario § 5: niente "Proforme", "Avvisi", sigle). */
const SOURCES: SourceDef[] = [
  { key: 'all',              label: 'Tutti i documenti',   icon: 'grid',    title: 'Fatture, note di credito, pre-fatture e avvisi di pagamento' },
  { key: 'FATTURA',          label: 'Solo le fatture',     icon: 'invoice', title: 'Solo le fatture, senza note di credito, pre-fatture e avvisi' },
  { key: 'NOTA_CREDITO',     label: 'Note di credito',     icon: 'refresh', title: 'Annullano in tutto o in parte una fattura' },
  { key: 'quote',            label: 'Preventivi',          icon: 'file',    title: 'Proposte ai clienti da firmare' },
  { key: 'PROFORMA',         label: 'Pre-fatture',         icon: 'send',    title: 'Anteprime di fattura (proforma): non valgono per il fisco' },
  { key: 'AVVISO_PAGAMENTO', label: 'Avvisi di pagamento', icon: 'bell',    title: 'Promemoria di pagamento per il cliente' },
];
// Tipi sempre visibili come pill; gli altri vivono nel menu '···' (overflow),
// e quello scelto dal menu viene promosso a pill finché resta attivo.
const MAIN_SOURCE_KEYS: DocSource[] = ['all', 'FATTURA', 'quote'];

/**
 * Stati: il server filtra su UN valore dell'enum, quindi niente gruppi
 * ("aperte" = EMESSA + INVIATA non si può chiedere in una query sola).
 * Le rate davvero da incassare hanno una vista tutta loro: /admin/fatture (Da incassare).
 */
// Le pill di stato sono filtri: ognuna nomina la cosa che lascia in lista
// (regola etichette di SPEC.md, niente aggettivi soli come "Emesse").
const STATUS_INVOICE = [
  { key: '',                        label: 'In ogni stato di pagamento' },
  { key: InvoiceStatus.EMESSA,      label: 'Da incassare dai clienti' },
  { key: InvoiceStatus.SCADUTA,     label: 'In ritardo col pagamento' },
  { key: InvoiceStatus.PAGATA,      label: 'Pagate dal cliente' },
  { key: InvoiceStatus.BOZZA,       label: 'Bozze non emesse' },
  { key: InvoiceStatus.INVIATA,     label: 'Inviate al cliente, da incassare' },
  { key: InvoiceStatus.ANNULLATA,   label: 'Annullate' },
];
const STATUS_QUOTE = [
  { key: '',                        label: 'In ogni stato della firma' },
  { key: QuoteStatus.INVIATO,       label: 'Inviati al cliente' },
  { key: QuoteStatus.ACCETTATO,     label: 'Firmati dal cliente' },
  { key: QuoteStatus.BOZZA,         label: 'Bozze non inviate' },
  { key: QuoteStatus.VISUALIZZATO,  label: 'Aperti dal cliente' },
  { key: QuoteStatus.CONVERTITO,    label: 'Diventati fattura' },
  { key: QuoteStatus.RIFIUTATO,     label: 'Rifiutati dal cliente' },
  { key: QuoteStatus.SCADUTO,       label: 'Scaduti senza firma' },
];
// Pill di stato mostrate ("Tutti" + la più usata); le altre nel menu '···',
// e quella scelta dal menu viene promossa a pill finché resta attiva.
const STATUS_PILL_COUNT = 2;

/**
 * Ordina per (24/9): la lista è paginata, quindi ordina l'API (`sort` su
 * /invoices e /quotes). I filtri di tipo e stato restano le pill di sempre.
 */
const ORDINI_FATTURE: OpzioneOrdina[] = [
  { key: 'recenti', label: 'Emesse più di recente prima' },
  { key: 'vecchie', label: 'Emesse da più tempo prima' },
  { key: 'scadenza', label: 'Scadenza del pagamento più vicina prima' },
  { key: 'importo', label: 'Prima gli importi più alti' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
];
const ORDINI_PREVENTIVI: OpzioneOrdina[] = [
  { key: 'recenti', label: 'Emessi più di recente prima' },
  { key: 'vecchie', label: 'Emessi da più tempo prima' },
  { key: 'validita', label: 'Scadenza della proposta più vicina prima' },
  { key: 'importo', label: 'Prima gli importi più alti' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
];

interface FicStatus { connected: boolean; companyName: string | null; }

export function DocumentsSection({ showHistory, scope, embedded }: {
  showHistory?: boolean;
  /**
   * Dentro Amministrazione › Fatture la lista è divisa in due viste:
   * 'fiscali' (fatture, note di credito, proforme, avvisi) e 'preventivi'.
   * Senza, la lista mostra tutti i tipi con le pill (pagina storica /invoices).
   */
  scope?: 'fiscali' | 'preventivi';
  /** La pagina che la ospita ha già "Nuova fattura o preventivo" in intestazione: qui non si ripete. */
  embedded?: boolean;
} = {}) {
  const user = useAuthStore((s) => s.user);
  const canWrite = useMemo(() => {
    if (!user) return false;
    const lvl = (user.permissions.find((p) => p.module === ModuleKey.FATTURAZIONE)?.level ?? PermissionLevel.NONE) as PermissionLevel;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  }, [user]);

  const [typeParam] = useUrlState<string>('type', 'all');
  const sources = scope === 'fiscali' ? SOURCES.filter((s) => s.key !== 'quote') : SOURCES;
  const source: DocSource = scope === 'preventivi' ? 'quote' : (sources.find((s) => s.key === typeParam) ?? sources[0]!).key;
  const [statusParam] = useUrlState<string>('status', '');
  const statusFilters = source === 'quote' ? STATUS_QUOTE : STATUS_INVOICE;
  // Uno stato di un'altra fonte (es. ?type=quote&status=PAGATA) non esiste qui: torna a "Tutti"
  const status = statusFilters.some((f) => f.key === statusParam) ? statusParam : '';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const ordina = useOrdina(source === 'quote' ? ORDINI_PREVENTIVI : ORDINI_FATTURE);
  useEffect(() => { setPage(1); }, [source, status, search, showHistory, ordina.key]);

  // Aggiornamento atomico di più param (un solo history entry, niente race tra
  // setter consecutivi): cambiando tipo lo stato riparte da "Tutti".
  const setParamsBatch = (next: Record<string, string>) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) {
        if (!v || v === 'all') sp.delete(k);
        else sp.set(k, v);
      }
      return sp;
    });
  };
  const chooseSource = (k: DocSource) => setParamsBatch({ type: k, status: '' });
  const chooseStatus = (k: string) => setParamsBatch({ status: k });

  const params = useMemo(
    () => ({ source, status, search: search.trim(), sort: ordina.key, includeHistory: showHistory, page, pageSize: PAGE_SIZE }),
    [source, status, search, ordina.key, showHistory, page],
  );
  const q = useQuery(documentsQuery(params));
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: () => api.get<FicStatus>('/settings/integrations/fic/status'),
  });

  const rows = q.data?.rows ?? [];
  const filtered = !!status || !!search.trim();
  const pillSources = sources.filter((s) => MAIN_SOURCE_KEYS.includes(s.key) || s.key === source);
  const overflowSources = sources.filter((s) => !MAIN_SOURCE_KEYS.includes(s.key));
  // Nella vista Preventivi il tipo è uno solo: niente pill del tipo.
  const showTypes = scope !== 'preventivi';
  const pillStatuses = statusFilters.filter((f, i) => i < STATUS_PILL_COUNT || f.key === status);
  const overflowStatuses = statusFilters.slice(STATUS_PILL_COUNT);

  return (
    <>
      {/* Sul telefono i filtri stanno dietro un tasto solo: in fila facevano
          una barra alta 223px che spingeva la lista sotto la piega
          (mobile.md §5). Sul computer MobileFilters restituisce i figli
          così come sono, senza nemmeno un div intorno. */}
      <MobileFilters attivi={[showTypes && source !== 'all', !!status, !!search].filter(Boolean).length} label="Cerca, ordina e filtra">
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        {/* Filtro tipo documento — pill principali + menu '···' */}
        {showTypes && <>
        <div className="flex items-center gap-1.5" role="group" aria-label="Tipo documento">
          {pillSources.map((s) => (
            <button
              key={s.key}
              onClick={() => chooseSource(s.key)}
              aria-pressed={source === s.key}
              title={s.title}
              className={source === s.key ? 'filter-pill is-active' : 'filter-pill'}
            >
              {s.label}
            </button>
          ))}
          <ActionsMenu
            variant="default"
            label="Altri tipi di documento"
            excludeKey={source}
            items={overflowSources.map((s) => ({
              key: s.key,
              label: s.label,
              icon: s.icon,
              onClick: () => chooseSource(s.key),
            }))}
          />
        </div>

        <div className="divider" />
        </>}

        <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
          <Icon name="search" size={12} />
          <input
            aria-label="Cerca documenti"
            placeholder={source === 'quote' ? 'Cerca per numero, titolo o note…' : 'Cerca per numero o note…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Filtro stato — pill principali + menu '···' (stesso pattern del tipo) */}
        <div className="flex items-center gap-1.5" role="group" aria-label="Stato documento">
          {pillStatuses.map((f) => (
            <button
              key={f.key || 'all'}
              onClick={() => chooseStatus(f.key)}
              aria-pressed={status === f.key}
              className={status === f.key ? 'filter-pill is-active' : 'filter-pill'}
            >
              {f.label}
            </button>
          ))}
          <ActionsMenu
            variant="default"
            label={source === 'quote' ? 'Altri stati della firma' : 'Altri stati del pagamento'}
            excludeKey={status}
            items={overflowStatuses.map((f) => ({
              key: f.key,
              label: f.label,
              icon: 'filter' as const,
              onClick: () => chooseStatus(f.key),
            }))}
          />
        </div>

        <span className="mute text-[12px]">{source === 'quote' ? ((q.data?.total ?? 0) === 1 ? '1 preventivo' : `${q.data?.total ?? 0} preventivi`) : ((q.data?.total ?? 0) === 1 ? '1 documento' : `${q.data?.total ?? 0} documenti`)}</span>
        {/* Il gemello in fondo allo schermo è la barra d'azione qui sotto:
            in cima il pollice non ci arriva (mobile.md §4). */}
        <OrdinaFiltra ordina={ordina} />
        {canWrite && !embedded && <span className="d-only"><NewDocumentButton size="sm" /></span>}
      </div>
      </MobileFilters>

      {canWrite && !embedded && (
        <DesktopOnly
          cosa="La creazione di fatture e preventivi"
          perche="è una procedura a più passaggi con righe, imposte e scadenze; qui puoi consultare le fatture che ci sono e segnarle incassate."
        >
          <div className="m-actionbar m-only">
            <NewDocumentButton />
          </div>
        </DesktopOnly>
      )}

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={8} cols={7} withAvatar={false} /></tbody></table></div>
      ) : rows.length === 0 ? (
        <DocumentsEmpty source={source} filtered={filtered} canWrite={canWrite} onReset={() => setParamsBatch({ status: '', q: '' })} />
      ) : (
        <div className="ed-section">
          {/* tbl--card: su 390 le otto colonne facevano una tabella larga
              1216px dentro 358, che scorreva di lato nascondendo metà dei
              dati (mobile.md §3). Sul computer non cambia niente: tbl--card
              non ha regole fuori dalla media query. */}
          <table className="tbl tbl--ed tbl--card">
            <thead>
              <tr>
                {source === 'all' && <th style={{ width: 170 }}>Tipo di documento</th>}
                <th>Numero e cliente</th>
                <th>Progetto</th>
                <th>{source === 'quote' ? 'Creato il' : 'Emesso il'}</th>
                <th>
                  {source === 'quote' ? 'Valido fino al' : 'Da pagare entro'}
                  <Helper text={source === 'quote'
                    ? 'Dopo questa data il preventivo non impegna più: se il cliente firma dopo, conviene rifarlo.'
                    : 'La data entro cui il cliente deve pagare. Con più rate è la prossima.'} />
                </th>
                <th className="num">Totale con IVA</th>
                <th>{source === 'quote' ? 'Stato della firma' : 'Stato del pagamento'}</th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <DocumentRow key={`${r.kind}-${r.id}`} r={r} ficConnected={!!ficStatus?.connected} showType={source === 'all'} canWrite={canWrite} />
              ))}
            </tbody>
          </table>
          {q.data && <Pagination page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} totalPages={q.data.totalPages} onPage={setPage} />}
        </div>
      )}
    </>
  );
}

// Primo-uso reale (nessun documento di quel tipo) vs filtro a vuoto: solo nel
// primo caso ha senso la CTA d'innesco.
function DocumentsEmpty({ source, filtered, canWrite, onReset }: {
  source: DocSource; filtered: boolean; canWrite: boolean; onReset: () => void;
}) {
  const quote = source === 'quote';
  if (filtered) {
    return (
      <EmptyState
        brand
        title={source === 'quote' ? 'Nessun preventivo con questi filtri' : 'Nessuna fattura con questi filtri'}
        description={source === 'quote' ? 'Prova col numero del preventivo, o azzera i filtri per vederli tutti.' : 'Prova col numero della fattura, o azzera i filtri per vederle tutte.'}
        action={<button className="btn" onClick={onReset}>Azzera i filtri</button>}
      />
    );
  }
  return (
    <EmptyState
      brand
      title={quote ? 'Nessun preventivo' : 'Nessuna fattura emessa'}
      description={quote
        ? 'I preventivi che crei compaiono qui, con lo stato della firma.'
        : 'Le fatture che emettiamo compaiono qui, anche quelle che arrivano da FattureInCloud.'}
      action={canWrite
        ? (
          <button className="btn btn-primary d-only" onClick={() => useModalStore.getState().open(quote ? 'newQuote' : 'newInvoice')}>
            <Icon name="plus" size={12} /> {quote ? 'Crea il primo preventivo' : 'Crea la prima fattura'}
          </button>
        )
        : undefined}
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// "Nuova fattura o preventivo": il menu dei documenti che si possono creare
// ════════════════════════════════════════════════════════════════════
export function NewDocumentButton({ size = 'md' }: { size?: 'md' | 'sm' }) {
  const [open, setOpen] = useState(false);
  const openModal = useModalStore((s) => s.open);

  const newQuote = () => { setOpen(false); openModal('newQuote'); };
  const newInvoice = (type?: InvoiceType) => { setOpen(false); openModal('newInvoice', type ? { type } : undefined); };

  return (
    <div className="relative">
      <button className={size === 'sm' ? 'btn btn-sm' : 'btn btn-primary'} onClick={() => setOpen((v) => !v)}>
        <Icon name="plus" size={size === 'sm' ? 10 : 14} /> Nuova fattura o preventivo <Icon name="chevronD" size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[61] rounded-[var(--r-card)] border border-line bg-white shadow-lg overflow-hidden" style={{ minWidth: 360 }}>
            <div className="p-2 border-b border-line">
              <div className="t-micro mb-1.5">Proposta al cliente</div>
              <DocChoice icon="file" title="Preventivo" sub="Il cliente lo firma online, gratis" onClick={newQuote} />
            </div>
            <div className="p-2">
              <div className="t-micro mb-1.5">Documenti di fatturazione</div>
              <DocChoice icon="invoice" title="Fattura"             sub="Fattura vera, con numero: vale per il fisco"  onClick={() => newInvoice(InvoiceType.FATTURA)} />
              <DocChoice icon="file"    title="Nota di credito"     sub="Annulla in tutto o in parte una fattura"       onClick={() => newInvoice(InvoiceType.NOTA_CREDITO)} />
              <DocChoice icon="send"    title="Pre-fattura"         sub="Anteprima di fattura (proforma): non vale per il fisco" onClick={() => newInvoice(InvoiceType.PROFORMA)} />
              <DocChoice icon="bell"    title="Avviso di pagamento" sub="Promemoria di pagamento per il cliente"        onClick={() => newInvoice(InvoiceType.AVVISO_PAGAMENTO)} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DocChoice({ icon, title, sub, onClick }: { icon: IconName; title: string; sub: string; onClick: () => void }) {
  return (
    <div className="flex items-center gap-2.5 p-2.5 hover:bg-paper-2 cursor-pointer rounded-[var(--r-ctl)] transition" onClick={onClick}>
      <div className="icon-tile sm">
        <Icon name={icon} size={16} />
      </div>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="mute text-[12px]">{sub}</div>
      </div>
      <Icon name="arrowRight" size={12} className="mute" />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Riga documento
// ════════════════════════════════════════════════════════════════════

/** Le rate di una fattura, come le manda GET /invoices (vedi invoices.routes.ts). */
interface RataLite { id: string; seq: number; amount: string | number; status: string; dueDate: string | null }

/**
 * La rata che "Segna incassata" chiude: la prossima aperta. Con più rate aperte si
 * incassa una rata alla volta, come in Fatture da incassare: un bonifico paga
 * una rata. Senza rate (documenti solo locali) si incassa la fattura intera.
 */
function prossimaRata(r: UnifiedRow): { rata: RataLite | null; di: number; aperte: number } {
  const rate = ((r.raw as InvoiceItem & { payments?: RataLite[] }).payments ?? []).filter((x) => x.status !== 'STORNATA');
  const aperte = rate.filter((x) => x.status === 'DA_INCASSARE');
  return { rata: aperte[0] ?? null, di: rate.length, aperte: aperte.length };
}

function DocumentRow({ r, ficConnected, showType, canWrite }: { r: UnifiedRow; ficConnected: boolean; showType: boolean; canWrite: boolean }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { issueInvoiceLocal, pushInvoiceFic, markInvoicePaid, convertQuote } = useDocActions();
  const { ignoreDocs } = useAdminActions();
  const [aperto, setAperto] = useState<null | 'incassa' | 'togli'>(null);
  // Movimenti in banca e conti sono dell'Amministrazione: senza, il popover
  // resta con data, metodo e nota (niente errori per chi ha la sola Fatturazione).
  const conBanca = canReadAdmin(user);

  // Solo dal giorno dopo la scadenza: una fattura che scade oggi non è ancora in ritardo.
  const overdue = r.secondaryDate
    && (daysFromToday(r.secondaryDate) ?? 0) < 0
    && !['PAGATA', 'ACCETTATO', 'CONVERTITO', 'RIFIUTATO', 'SCADUTO', 'ANNULLATA'].includes(r.status);
  // Una fattura da incassare con la data per pagare già passata è in ritardo,
  // anche se a sistema lo stato è ancora EMESSA o INVIATA: il chip lo dice.
  const inRitardo = r.kind === 'invoice' && overdue && ['EMESSA', 'INVIATA'].includes(r.status);
  const info = inRitardo ? STATUS_INFO.SCADUTA! : STATUS_INFO[r.status] ?? { label: r.status, tone: 'default' as const };
  const detailPath = r.kind === 'quote' ? `/quotes/${r.id}` : `/invoices/${r.id}`;
  // Preventivo convertito: link diretto alla fattura generata
  const convertedTo = r.kind === 'quote' && r.status === 'CONVERTITO'
    ? (r.raw as QuoteItem).convertedToInvoice
    : null;
  const { rata, di, aperte } = r.kind === 'invoice' ? prossimaRata(r) : { rata: null, di: 0, aperte: 0 };
  const importoIncasso = rata ? Number(rata.amount) : Number(r.total);
  const label = `Fattura ${r.number ?? '—'} · ${r.client.legalName}`;

  return (
    <tr className="cursor-pointer hover:bg-paper-2" onClick={() => navigate(detailPath)}>
      {showType && (
        <td data-m="key" data-l="Tipo di documento">
          <RowTypeBadge kind={r.kind} invoiceType={r.invoiceType} />
        </td>
      )}
      <td data-m="hd">
        <div className="mono font-semibold">{r.number ?? '—'}</div>
        <div className="text-[12px] mute">{r.client.legalName}</div>
        {convertedTo && (
          <a
            className="mono text-[12px] mute underline cursor-pointer"
            onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${convertedTo.id}`); }}
          >
            fattura {convertedTo.number ?? '—'} →
          </a>
        )}
      </td>
      {/* Progetto e data di emissione si leggono aprendo il documento: in
          scheda restano fuori per non superare le quattro informazioni chiave
          di mobile.md §3. La scadenza invece resta, perché è quella che si
          colora di rosso quando è passata. */}
      <td data-m="off" className="mono mute-2">{r.project?.code ?? '—'}</td>
      <td data-m="off" className="mono mute">{fmtDate(r.primaryDate)}</td>
      <td data-m="key" data-l={r.kind === 'quote' ? 'Valido fino al' : 'Da pagare entro'} className={`mono ${overdue ? 'text-alert' : 'mute'}`}>{fmtDate(r.secondaryDate)}</td>
      <td data-m="key" data-l="Totale con IVA" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.total)}</td>
      <td data-m="key" data-l={r.kind === 'quote' ? 'Stato della firma' : 'Stato del pagamento'}><Chip tone={info.tone} dot>{info.label}</Chip></td>
      {/* I gesti rapidi (emetti, incassa, crea la fattura) prendono una riga
          loro: è il motivo per cui questa schermata si apre dal telefono. */}
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end items-center gap-1 relative">
          <RowActions
            row={r}
            ficConnected={ficConnected}
            onIssue={() => issueInvoiceLocal.mutate(r.id)}
            onPushFic={() => pushInvoiceFic.mutate(r.id)}
            onMarkPaid={() => setAperto(aperto === 'incassa' ? null : 'incassa')}
            onConvert={() => convertQuote.mutate({ id: r.id, total: Number(r.total) })}
            rataLabel={rata && di > 1 ? `rata ${rata.seq} di ${di}` : null}
          />
          {/* Togli dall'elenco solo sui documenti di fatturazione: i preventivi
              si eliminano dal loro dettaglio e non entrano nei conti. */}
          {canWrite && r.kind === 'invoice' && (
            <ActionsMenu items={[{ key: 'trash', label: "Togli la fattura dall'elenco", icon: 'trash', danger: true, onClick: () => setAperto('togli') }]} />
          )}
          {aperto === 'incassa' && (
            <SettlePopover
              mode="incassa"
              amount={importoIncasso}
              subtitle={rata && di > 1 ? `rata ${rata.seq} di ${di} della fattura ${r.number ?? ''}` : `fattura ${r.number ?? ''}`}
              target={conBanca && rata ? { kind: 'INVOICE_PAYMENT', id: rata.id } : undefined}
              counterparty={r.client.legalName}
              side="CREDIT"
              adminData={conBanca}
              withNote
              // Con l'ultima rata la fattura risulta pagata e parte la mail di avviso (invoices.routes.ts).
              avviso={aperte <= 1 ? `${aperte === 1 && di > 1 ? "È l'ultima rata da incassare. " : ''}${AVVISO_INCASSO}` : undefined}
              pending={markInvoicePaid.isPending}
              onClose={() => setAperto(null)}
              onConfirm={(v) => {
                setAperto(null);
                markInvoicePaid.mutate({ id: r.id, total: importoIncasso, paymentId: rata?.id, label, ...v });
              }}
            />
          )}
          {aperto === 'togli' && (
            <TrashPopover
              title={r.number ?? '—'}
              pending={ignoreDocs.isPending}
              onClose={() => setAperto(null)}
              onConfirm={(v) => { setAperto(null); ignoreDocs.mutate({ kind: 'invoice', ids: [r.id], label, ...v }); }}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

// Indice di tipo documento: tessera monocroma (mai un colore per tipo): il
// colore della riga resta UNO solo, lo Stato. Parole intere, niente sigle
// ("NC", "Prof.") che chi non è del mestiere deve decifrare.
export function RowTypeBadge({ kind, invoiceType }: { kind: DocKind; invoiceType: InvoiceItem['type'] | null }) {
  let label = '—';
  let icon: IconName = 'file';
  if (kind === 'quote') { label = 'Preventivo'; icon = 'file'; }
  else if (invoiceType === 'FATTURA')          { label = 'Fattura';             icon = 'invoice'; }
  else if (invoiceType === 'NOTA_CREDITO')     { label = 'Nota di credito';     icon = 'file'; }
  else if (invoiceType === 'PROFORMA')         { label = 'Pre-fattura';         icon = 'send'; }
  else if (invoiceType === 'AVVISO_PAGAMENTO') { label = 'Avviso di pagamento'; icon = 'bell'; }
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-6 h-6 grid place-items-center rounded-[var(--r-tile)] bg-paper-2 text-ink">
        <Icon name={icon} size={12} />
      </div>
      <span className="text-[12px]">{label}</span>
    </div>
  );
}

// Azione di stato contestuale (una sola, con label): Emetti la fattura / Segna incassata / Crea la fattura.
// Niente freccia 'Apri': il click sulla riga naviga già al dettaglio.
function RowActions({
  row, ficConnected, onIssue, onPushFic, onMarkPaid, onConvert, rataLabel,
}: {
  row: UnifiedRow; ficConnected: boolean;
  onIssue: () => void; onPushFic: () => void; onMarkPaid: () => void; onConvert: () => void;
  /** "rata 1 di 3": con più rate si incassa la prossima, e il tooltip lo dice. */
  rataLabel?: string | null;
}) {
  if (row.kind === 'invoice' && row.status === 'BOZZA') {
    return (
      <button
        className="btn btn-sm"
        title={ficConnected ? 'Emetti la fattura su FattureInCloud: prende il numero ufficiale' : 'Emetti la fattura qui, con la numerazione interna'}
        onClick={ficConnected ? onPushFic : onIssue}
      >
        <Icon name="send" size={10} /> Emetti la fattura
      </button>
    );
  }
  if (row.kind === 'invoice' && ['EMESSA', 'INVIATA', 'SCADUTA'].includes(row.status)) {
    return (
      <button className="btn btn-sm" onClick={onMarkPaid}
        title={`${rataLabel ? `Segna incassata la ${rataLabel}` : 'Segna incassata la fattura'} in Masamune OS: non parte nessun pagamento e FattureInCloud non cambia`}>
        <Icon name="check" size={10} /> Segna incassata
      </button>
    );
  }
  if (row.kind === 'quote' && row.status === 'ACCETTATO') {
    return (
      <button className="btn btn-sm" title="Il cliente ha firmato: crea la fattura da questo preventivo" onClick={onConvert}>
        <Icon name="invoice" size={10} /> Crea la fattura
      </button>
    );
  }
  return null;
}
